import React, { useEffect, useRef, useState } from 'react';

// Scroll-reveal wrapper: children start hidden (faded + shifted) and animate
// into view the first time they scroll into the viewport. Uses a single
// IntersectionObserver per element and reveals once (then stops observing), so
// it's cheap and content never flickers back out on scroll-up.
//
// Usage:
//   <Reveal>...</Reveal>                      // default: fade + rise
//   <Reveal variant="left" delay={100}>...</Reveal>
//   <Reveal as="section" className="py-16">...</Reveal>
//
// variant: 'up' (default) | 'left' | 'right' | 'zoom'
// delay:   ms to stagger this element after it enters view
// once:    reveal a single time (default true); false lets it re-hide on exit
export default function Reveal({
  children,
  as: Tag = 'div',
  variant = 'up',
  delay = 0,
  once = true,
  className = '',
  style,
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // If the browser can't observe (very old) or the user prefers reduced
    // motion, just show the content — the CSS also forces it visible, but this
    // avoids leaving it stuck hidden if JS/CSS ever disagree.
    const prefersReduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            if (once) obs.unobserve(entry.target);
          } else if (!once) {
            setShown(false);
          }
        });
      },
      // Trigger a touch before the element is fully on-screen so the motion
      // reads as it arrives, not after. rootMargin trims the bottom so it fires
      // ~12% up from the fold.
      { threshold: 0.12, rootMargin: '0px 0px -12% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);

  const variantClass = variant === 'left' ? 'reveal-left'
    : variant === 'right' ? 'reveal-right'
    : variant === 'zoom' ? 'reveal-zoom'
    : '';

  return (
    <Tag
      ref={ref}
      className={`reveal ${variantClass} ${shown ? 'reveal-in' : ''} ${className}`.trim()}
      style={delay ? { ...style, transitionDelay: `${delay}ms` } : style}
      {...rest}
    >
      {children}
    </Tag>
  );
}
